import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import backIcon from "../assets/ecommerce/atras.png";
import { useMedioMayoristaCart } from "../context/MedioMayoristaCartContext";
import OtherProductsCarousel from "../components/Ecommerce/OtherProductsCarousel";
import {
  fetchMedioMayoristaProducts,
  toCarouselItem,
} from "../components/Ecommerce/medioMayoristaCatalog";

const formatCurrency = new Intl.NumberFormat("es-MX", {
  style: "currency",
  currency: "MXN",
  minimumFractionDigits: 2,
});

const parseNumber = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const normalizeString = (value) => {
  if (value === undefined || value === null) return null;
  const trimmed = String(value).trim();
  return trimmed.length ? trimmed : null;
};

const splitColors = (colors) =>
  (normalizeString(colors) || "")
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);

const toTitleCase = (value) =>
  value
    .toLowerCase()
    .split(" ")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const MedioMayoristaFlakesDetail = () => {
  const { id } = useParams();
  const { addItem, openCart, items } = useMedioMayoristaCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [otherProducts, setOtherProducts] = useState([]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [id]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");

    Promise.all([
      fetchMedioMayoristaProducts("flakes", "No se pudieron cargar los detalles de los flakes."),
      fetchMedioMayoristaProducts("resina", "No se pudieron cargar los productos de resina."),
    ])
      .then(([flakes, resins]) => {
        if (!active) return;
        const list = Array.isArray(flakes) ? flakes : [];
        const productId = Number(id);
        const match = list.find((item) => Number(item.id) === productId);
        if (!match) {
          throw new Error("No encontramos este producto de flakes.");
        }
        setProduct(match);
        setOtherProducts([
          ...(Array.isArray(resins) ? resins : []).map((item) => toCarouselItem(item, "resina")),
          ...list
            .filter((item) => Number(item.id) !== productId)
            .map((item) => toCarouselItem(item, "flakes")),
        ]);
      })
      .catch((fetchError) => {
        if (!active) return;
        setError(fetchError.message || "No se pudieron cargar los detalles de los flakes.");
        setOtherProducts([]);
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [id]);

  const imageUrl = useMemo(
    () => normalizeString(product?.image_url) || "/ecommerce/Flakes.png",
    [product]
  );
  const galleryImages = useMemo(() => {
    const raw = product?.images;
    if (Array.isArray(raw)) {
      const valid = raw.map(normalizeString).filter(Boolean);
      if (valid.length) return valid;
    }
    return [imageUrl];
  }, [imageUrl, product?.images]);

  useEffect(() => {
    setActiveImage(galleryImages[0] || imageUrl);
  }, [galleryImages, imageUrl]);

  const colorOptions = useMemo(() => splitColors(product?.colors), [product?.colors]);

  useEffect(() => {
    setSelectedColor("");
  }, [product?.id]);

  const basePrice = parseNumber(product?.base_price);
  const finalPrice = parseNumber(product?.final_price, basePrice);
  const discount = parseNumber(product?.discount);
  const discountPercent = Math.max(0, Math.round(discount * 100));
  const savings = Math.max(0, basePrice - finalPrice);

  const unitG = normalizeString(product?.unit_g);
  const unitGLabel = unitG ? `${unitG} g` : "desconocido";

  const deliveryRangeLabel = useMemo(() => {
    const now = new Date();
    const start = new Date(now);
    const end = new Date(now);
    start.setDate(now.getDate() + 5);
    end.setDate(now.getDate() + 7);
    const formatter = new Intl.DateTimeFormat("es-MX", { month: "short", day: "numeric" });
    return `${formatter.format(start)} y ${formatter.format(end)}`;
  }, []);

  const description =
    normalizeString(product?.description) || "[Aquí debe ir la descripción del producto.]";
  const presentations =
    normalizeString(product?.presentations) || "[Aquí deben ir las presentaciones.]";
  const compatibility =
    normalizeString(product?.compatibility) || "[Aquí debe ir la compatibilidad de procesos.]";
  const technicalSheetUrl = normalizeString(product?.technical_sheet_url);

  const canAddToCart = colorOptions.length === 0 || selectedColor !== "";

  const addCurrentProduct = () => {
    if (!product || !canAddToCart) return;
    const name = selectedColor ? `${product.name} - ${selectedColor}` : product.name;
    addItem({
      type: "medio-mayorista-flakes",
      name,
      unitPrice: finalPrice,
      quantity,
      payload: {
        flake_id: product.id,
        ...(selectedColor ? { color: selectedColor } : {}),
      },
    });
    openCart();
  };

  if (loading) {
    return (
      <section className="min-h-screen bg-white px-4 py-8 md:px-8">
        <div className="mx-auto max-w-[1180px] rounded-xl border border-gray-200 p-6 text-center text-gray-600">
          Cargando detalle de flakes...
        </div>
      </section>
    );
  }

  if (error || !product) {
    return (
      <section className="min-h-screen bg-white px-4 py-8 md:px-8">
        <div className="mx-auto max-w-[1180px] rounded-xl border border-red-300 bg-red-50 p-6 text-center text-red-700">
          {error || "No se encontró el producto."}
        </div>
      </section>
    );
  }

  return (
    <section className="relative min-h-screen bg-white px-4 py-6 md:px-8 md:py-8">
      <div className="fixed right-3 md:right-8 top-[96px] md:top-[104px] z-30">
        <button type="button" onClick={openCart} className="relative rounded-full">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-green shadow-[0_10px_22px_rgba(0,0,0,0.35)] md:h-16 md:w-16">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-7 w-7 text-white md:h-9 md:w-9"
            >
              <circle cx="9" cy="20" r="1.6" />
              <circle cx="18" cy="20" r="1.6" />
              <path d="M2 2h2l2.4 11.5a2 2 0 0 0 2 1.6h8.8a2 2 0 0 0 2-1.6L21 6H7" />
            </svg>
          </span>
          {!!items.length && (
            <span className="absolute -top-2 -right-2 flex h-7 min-w-[28px] items-center justify-center rounded-full bg-[#ff3030] px-1.5 text-sm font-bold text-white">
              {items.length}
            </span>
          )}
        </button>
      </div>

      <div className="mx-auto max-w-[1180px] text-left">
        <div className="mt-4 grid grid-cols-1 gap-7 md:grid-cols-[470px,1fr] md:gap-10">
          <div className="md:col-span-2">
            <div className="w-full md:w-[470px]">
              <Link
                to="/productos/Flakes"
                className="mb-1 inline-flex items-center gap-2 text-[#6f7781] hover:text-[#4f5560]"
              >
                <img src={backIcon} alt="Volver" className="h-9 w-9 opacity-95" />
                <span className="text-lg font-extrabold uppercase tracking-wide">
                  Volver a "Flakes"
                </span>
              </Link>
            </div>
          </div>

          <div>
            <div className="relative overflow-hidden rounded-md border border-gray-300 bg-white">
              {discountPercent > 0 && (
                <div className="absolute left-2 top-2 z-10 rounded-full bg-[#e71318] px-2.5 py-1 leading-none text-white shadow-lg">
                  <span className="text-[22px] font-extrabold">-{discountPercent}</span>
                  <span className="pl-0.5 align-top text-xs font-bold">%</span>
                  <span className="block -mt-0.5 text-[13px] font-extrabold uppercase">off</span>
                </div>
              )}
              <img
                src={activeImage || imageUrl}
                alt={product.name}
                className="h-[320px] w-full object-contain p-3 md:h-[520px]"
                onError={(event) => {
                  event.currentTarget.onerror = null;
                  event.currentTarget.src = "/ecommerce/Flakes.png";
                }}
              />
            </div>

            <div className="mt-3 flex gap-3">
              {galleryImages.slice(0, 4).map((imgSrc, index) => {
                const isActive = activeImage === imgSrc;
                return (
                  <button
                    key={`${imgSrc}-${index}`}
                    type="button"
                    onClick={() => setActiveImage(imgSrc)}
                    className={`h-16 w-16 overflow-hidden rounded border ${
                      isActive ? "border-black" : "border-gray-300"
                    }`}
                  >
                    <img
                      src={imgSrc}
                      alt={`${product.name} miniatura ${index + 1}`}
                      className="h-full w-full object-cover"
                    />
                  </button>
                );
              })}
            </div>
          </div>

          <div className="pb-8 text-left">
            <h1 className="text-3xl font-extrabold uppercase leading-[0.95] text-black md:text-5xl">
              {product.name}
            </h1>
            <h2 className="mt-1 text-xl uppercase tracking-tight text-[#111827] md:text-3xl">
              OMOVA CARBON FIBER
            </h2>

            <div className="mt-4">
              <label htmlFor="flake-color" className="block text-lg text-[#111827] md:text-2xl">
                Elige el color:
              </label>
              <select
                id="flake-color"
                value={selectedColor}
                onChange={(event) => setSelectedColor(event.target.value)}
                disabled={!colorOptions.length}
                className="mt-2 w-full max-w-[320px] border border-[#bdbdbd] bg-white px-3 py-2 text-lg font-bold text-[#222] disabled:bg-[#f2f2f2] disabled:text-[#999]"
              >
                <option value="">{colorOptions.length ? "COLOR" : "Sin opciones"}</option>
                {colorOptions.map((color) => (
                  <option key={color} value={color}>
                    {toTitleCase(color)}
                  </option>
                ))}
              </select>
            </div>

            {savings > 0 && (
              <div className="mt-4 w-fit bg-[#e71318] px-4 py-1 text-xl font-extrabold leading-none text-white md:text-2xl">
                Ahorra {formatCurrency.format(savings)}
              </div>
            )}

            <div className="mt-3 flex flex-wrap items-end gap-x-3 gap-y-1">
              <span className="text-4xl font-extrabold leading-none text-[#df1414] md:text-5xl">
                {formatCurrency.format(finalPrice)}
                <sup className="text-lg md:text-xl">*</sup>
              </span>
              {basePrice > finalPrice && (
                <span className="text-2xl leading-none text-[#b9b9b9] line-through md:text-3xl">
                  {formatCurrency.format(basePrice)}
                </span>
              )}
            </div>

            <div className="mt-4 flex flex-nowrap items-center gap-3">
              <div className="inline-flex overflow-hidden rounded border border-[#cccccc] bg-[#f2f2f2] text-[#222]">
                <button
                  type="button"
                  onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}
                  className="px-4 py-1 text-xl leading-none hover:bg-[#e7e7e7]"
                >
                  -
                </button>
                <span className="px-5 py-1 text-xl leading-none">{quantity}</span>
                <button
                  type="button"
                  onClick={() => setQuantity((prev) => prev + 1)}
                  className="px-4 py-1 text-xl leading-none hover:bg-[#e7e7e7]"
                >
                  +
                </button>
              </div>

              <button
                type="button"
                disabled={!canAddToCart}
                onClick={addCurrentProduct}
                className="whitespace-nowrap rounded bg-primary-green px-5 py-2 text-base font-bold uppercase leading-none tracking-wide text-white transition hover:brightness-105 disabled:opacity-50 disabled:cursor-not-allowed md:text-lg"
              >
                Agregar al carrito
              </button>
            </div>

            <div className="mt-4 space-y-1 text-sm text-[#333] md:text-base">
              <p>IVA incluido. El costo de envío se calcula en el carrito.</p>
              <p className="font-semibold">*Costo por bote. Unidad = ({unitGLabel})</p>
            </div>

            <div className="mt-2 inline-flex items-center gap-3 bg-black px-3 py-1.5 text-sm font-semibold text-white md:text-base">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-5 w-5 shrink-0"
              >
                <rect x="1" y="3" width="15" height="13" />
                <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                <circle cx="5.5" cy="18.5" r="1.5" />
                <circle cx="18.5" cy="18.5" r="1.5" />
              </svg>
              <span>Tu pedido llegaría entre {deliveryRangeLabel}</span>
            </div>

            <p className="mt-4 text-base text-gray-500 md:text-2xl">
              <Link to="/form" className="font-semibold text-primary-green underline">
                Regístrate
              </Link>{" "}
              para obtener acceso a este y más productos en nuestra plataforma mayorista
            </p>

            <div className="mt-6">
              <h3 className="text-2xl font-extrabold text-[#111827] md:text-4xl">
                Información técnica:
              </h3>

              <p className="mt-2 text-lg leading-tight text-[#111827] md:text-2xl">{description}</p>
              <p className="mt-3 text-lg leading-tight text-[#111827] md:text-2xl">
                <span className="font-bold">Presentaciones:</span> {presentations}
              </p>
              <p className="mt-3 text-base leading-tight text-[#111827] md:text-xl">
                {compatibility}
              </p>
            </div>

            {technicalSheetUrl ? (
              <a
                href={technicalSheetUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-flex rounded-md bg-black px-5 py-2 text-lg font-bold text-white hover:bg-gray-900 md:text-xl"
              >
                Descargar ficha técnica
              </a>
            ) : (
              <button
                type="button"
                disabled
                className="mt-4 inline-flex cursor-not-allowed rounded-md bg-black/80 px-5 py-2 text-lg font-bold text-white md:text-xl"
              >
                Descargar ficha técnica
              </button>
            )}
          </div>
        </div>
      </div>
      <OtherProductsCarousel items={otherProducts} />
    </section>
  );
};

export default MedioMayoristaFlakesDetail;
